import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { API_URL } from "../config/env-var.config";
import { getImpersonateEmail } from "../storage-utils";

export const apiClient: AxiosInstance = Axios.create({
  baseURL: API_URL,
  paramsSerializer: {
    indexes: null,
  },
});

export type AuthTokens = { access?: string; id?: string };
export type TokenProvider = () => Promise<AuthTokens>;

// Reads the Amplify session straight from storage. Used until ApiProvider
// mounts and registers its own provider (which also syncs React auth state and
// logs the user out on an unrecoverable session error).
const defaultTokenProvider: TokenProvider = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    return {
      access: tokens?.accessToken?.toString(),
      id: tokens?.idToken?.toString(),
    };
  } catch {
    return {};
  }
};

let tokenProvider: TokenProvider = defaultTokenProvider;

export function setTokenProvider(provider: TokenProvider | null) {
  tokenProvider = provider ?? defaultTokenProvider;
}

// Auth headers are attached by a module-level interceptor rather than from a
// React effect. Requests fired before the provider tree has mounted its
// effects — TanStack Router loaders, queries started in child components'
// effects — would otherwise go out anonymously. That is how a buyer ended up
// streaming previews: /albums/detail resolved without a token, the API served
// the preview assets, and that response stayed in the query cache for the rest
// of the session.
apiClient.interceptors.request.use(
  async (config) => {
    const tokens = await tokenProvider();

    if (tokens?.access) {
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }

    if (tokens?.id) {
      config.headers["X-Id-Token"] = tokens.id;
    }

    const impersonateEmail = getImpersonateEmail();
    if (impersonateEmail) {
      config.headers["X-Impersonate-Email"] = impersonateEmail;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

export const apiRequest = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const source = Axios.CancelToken.source();
  const promise = apiClient({
    ...config,
    ...options,
    cancelToken: source.token,
  });

  // @ts-expect-error Missing type definition for 'cancel' property
  promise.cancel = () => {
    source.cancel("Query was cancelled");
  };

  return promise.then((response) => response.data);
};

export type ErrorType<Error> = AxiosError<Error>;

export type BodyType<BodyData> = BodyData;
